import React, { PropsWithChildren, useCallback, useContext, useRef } from 'react';

import { CustomBottomSheetModalProvider } from '@/feature/alerts/components/CustomBottomSheetModalProvider';

import { ShareEntryModal, ShareEntryModalRef, ShareEntryProps } from './ShareEntryModal';

type ShareEntry = {
    showShareEntryModal: (props: ShareEntryProps) => void;
    dismissShareEntryModal: () => void;
};

const ShareEntryContext = React.createContext<ShareEntry | undefined>(undefined);

/**
 * Provides access to the Modal used to display the share options/channels for your entry
 */
export const ShareEntryProvider: React.FC<PropsWithChildren> = ({ children }) => {
    const modalRef = useRef<ShareEntryModalRef>(null);

    const showShareEntryModal = useCallback((props: ShareEntryProps) => {
        modalRef.current?.show(props);
    }, []);

    const dismissShareEntryModal = useCallback(() => {
        modalRef.current?.dismiss();
    }, []);

    return (
        <CustomBottomSheetModalProvider>
            <ShareEntryContext.Provider value={{ showShareEntryModal, dismissShareEntryModal }}>
                {children}
                <ShareEntryModal ref={modalRef} />
            </ShareEntryContext.Provider>
        </CustomBottomSheetModalProvider>
    );
};

export const useShareEntry = () => {
    const context = useContext(ShareEntryContext);
    if (context === undefined) {
        throw new Error('useShareEntry must be used within a ShareEntryProvider');
    }
    return context;
};
